import type { AddToCart } from "../../../cbi-generator/generated/dto/AddToCart";
import { ItemType } from "../../../cbi-generator/generated/dto/ItemType";
import type { ProductListing } from "@/api/models/shop/ProductListing";
import type { PersonRef } from "../../../cbi-generator/generated/dto/PersonRef";
import type { MembershipRef } from "../../../cbi-generator/generated/dto/MembershipRef";

export type MemberCatalogPurchaseItem = {
  itemType?: ItemType | null;
  catalogItemInstance: Pick<
    ProductListing["catalogItemInstance"],
    | "assignId"
    | "allowPriceOverride"
    | "priceOverrideMin"
    | "priceOverrideMax"
    | "minQuantity"
    | "maxQuantity"
  >;
};

export interface MemberCatalogPurchaseInput {
  recipient?: PersonRef | null;
  quantity?: number;
  specifiedAmountCents?: number | null;
  cardNum?: string | null;
  balanceNumber?: string | null;
  membership?: MembershipRef | null;
}

export function buildMemberCatalogPurchase(
  item: MemberCatalogPurchaseItem,
  input: MemberCatalogPurchaseInput = {},
): AddToCart {
  const type = item.itemType;
  if (!type) throw new Error("This catalog item has no purchasable type.");
  if (type === ItemType.DONATION)
    throw new Error(
      "Donation fund and initiative fields are not supported by the member order API.",
    );
  if (type === ItemType.PAID_CLASS)
    throw new Error("Class enrollment must be completed through class signup.");

  const recipientRequired =
    type === ItemType.MEMBERSHIP ||
    type === ItemType.DAMAGE_WAIVER ||
    type === ItemType.GUEST_PRIVS;
  const recipient = input.recipient ?? null;
  if (recipientRequired && !recipient)
    throw new Error("Choose who this purchase is for.");
  if (
    recipient &&
    (!Number.isSafeInteger(recipient.personId) || recipient.personId <= 0)
  )
    throw new Error("Choose a valid recipient.");

  const quantity = type === ItemType.PRODUCT ? (input.quantity ?? 1) : 1;
  if (!Number.isSafeInteger(quantity) || quantity < 1)
    throw new Error("Quantity must be a positive whole number.");
  const itemInstance = item.catalogItemInstance;
  if (type === ItemType.PRODUCT) {
    if (quantity < (itemInstance.minQuantity ?? 1))
      throw new Error(`Minimum quantity is ${itemInstance.minQuantity}.`);
    if (itemInstance.maxQuantity != null && quantity > itemInstance.maxQuantity)
      throw new Error(`Maximum quantity is ${itemInstance.maxQuantity}.`);
  }

  const specifiedAmountCents =
    itemInstance.allowPriceOverride === true
      ? (input.specifiedAmountCents ?? null)
      : null;
  if (specifiedAmountCents != null) {
    if (!Number.isSafeInteger(specifiedAmountCents) || specifiedAmountCents < 0)
      throw new Error("Enter a valid nonnegative amount.");
    if (specifiedAmountCents < (itemInstance.priceOverrideMin ?? 0))
      throw new Error("Amount is below the catalog minimum.");
    if (
      itemInstance.priceOverrideMax != null &&
      specifiedAmountCents > itemInstance.priceOverrideMax
    )
      throw new Error("Amount exceeds the catalog maximum.");
  }
  if (type === ItemType.GUEST_PRIVS && !input.membership)
    throw new Error("Choose a membership for these guest privileges.");

  return {
    itemInstance: { assignId: itemInstance.assignId },
    person: recipient,
    quantity,
    specifiedAmountCents,
    parent: null,
    options: {
      cardNum: type === ItemType.CARD ? input.cardNum?.trim() || null : null,
      balanceNumber:
        type === ItemType.GIFT_CARD
          ? input.balanceNumber?.trim() || null
          : null,
      membership:
        type === ItemType.GUEST_PRIVS ? (input.membership ?? null) : null,
      donation: null,
    },
    updateLine: null,
  };
}
